import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import ProfileEditDataTypePill from 'app/components/CommonStyled/ProfileEditDataTypePill';
import { SkillIcon } from 'app/components/DetailsPages/shared/CommonGround/Icons';
import {
  AsteriskIcon,
  CheckVerifiedIcon,
  CodeBrowserIcon,
  RightArrow,
  Sparkle,
} from 'app/components/shared/icons';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import { LayoutGroup } from 'framer-motion';
import { createSkill, postSkills } from 'services/profile-editor';
import {
  profileDataSelector,
  setAllSkills,
  setSkills,
} from 'store/reducers/profile-editor/profile-editor-reducer';
import * as Yup from 'yup';

import useProfileEditorSkillsDrawer from '../../hooks/useProfileEditorSkillsDrawer';
import ProfileEditorDrawer, { ProfileEditorDrawerRefProps } from '../Drawer';
import { ProfileEditorDrawerPublishButton, ProfileEditorDrawerTitle } from '../styled';
import {
  PROFILE_EDITOR_DRAWER_SKILLS_TYPES,
  ProfileEditorDrawerSkillsContext,
  ProfileEditorDrawerSkillsType,
} from './ProfileEditorDrawerSkillsContext';
import {
  ProfileEditorDrawerSkillsBottomText,
  ProfileEditorDrawerSkillsBottomWrapper,
  ProfileEditorDrawerSkillsFeelingStuckAllCategoriesContainer,
  ProfileEditorDrawerSkillsFeelingStuckAllCategory,
  ProfileEditorDrawerSkillsFeelingStuckAllContainer,
  ProfileEditorDrawerSkillsFeelingStuckAllPillsContainer,
  ProfileEditorDrawerSkillsFeelingStuckButton,
  ProfileEditorDrawerSkillsFeelingStuckButtonText,
  ProfileEditorDrawerSkillsFeelingStuckContainer,
  ProfileEditorDrawerSkillsFeelingStuckRandomContainer,
  ProfileEditorDrawerSkillsFeelingStuckRandomPillsContainer,
  ProfileEditorDrawerSkillsFeelingStuckRandomShowMoreContainer,
  ProfileEditorDrawerSkillsFeelingStuckRandomShowMoreText,
  ProfileEditorDrawerSkillsFeelingStuckRandomTitle,
  ProfileEditorDrawerSkillsFeelingStuckTitle,
  ProfileEditorDrawerSkillsFeelingStuckWrapper,
  ProfileEditorDrawerSkillsHeaderContainer,
  ProfileEditorDrawerSkillsHeaderDescription,
  ProfileEditorDrawerSkillsHeaderWrapper,
  ProfileEditorDrawerSkillsTypeSwitcherContainer,
  ProfileEditorDrawerSkillsTypeSwitcherLabel,
  ProfileEditorDrawerSkillsTypeSwitcherUnderline,
  ProfileEditorDrawerSkillsTypeSwitcherWrapper,
} from './styled';

const SkillsBasic = lazy(() => import('./SkillsBasic'));
const SkillsSoftware = lazy(() => import('./SkillsSoftware'));
const SkillsCertifications = lazy(() => import('./SkillsCertifications'));

const MAX_SKILLS = 35;
const MAX_BASIC_SKILLS = 15;
const MAX_CERTIFICATIONS_SKILLS = 10;
const MAX_SOFTWARE_SKILLS = 10;

interface ProfileEditorDrawerSkillsProps {
  onClose: () => void;
}

export default function ProfileEditorDrawerSkills({ onClose }: ProfileEditorDrawerSkillsProps) {
  const { skills, allSkills } = useProfileEditorSkillsDrawer();
  const [optionsPicked, setOptionsPicked] = useState<any[]>(skills);
  const [skillsCreated, setSkillsCreated] = useState<any[]>([]);
  const [currentType, setCurrentType] = useState<ProfileEditorDrawerSkillsType>(
    PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL
  );
  const [highlightedSkillIdentifier, setHighlightedSkillIdentifier] = useState<string | null>(null);
  const drawerRef = useRef<ProfileEditorDrawerRefProps>(null);
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<any>>(null);

  useEffect(() => {
    setOptionsPicked(skills.map((skill) => ({ ...skill, label: skill.name })));

    const basicSkills = skills.filter((skill) => !skill.certification && !skill.software);
    const certificationsSkills = skills.filter((skill) => skill.certification);
    const softwareSkills = skills.filter((skill) => skill.software);

    formikRef.current.setFieldValue(
      'skills_collection',
      basicSkills.map((skill) => skill.uuid)
    );
    formikRef.current.setFieldValue(
      'certifications_collection',
      certificationsSkills.map((skill) => skill.uuid)
    );
    formikRef.current.setFieldValue(
      'software_collection',
      softwareSkills.map((skill) => skill.uuid)
    );
  }, [skills]);

  const skillsValidationSchema = Yup.object().shape({
    collection: Yup.array(),
  });

  const options = useMemo(() => {
    const allOptions = allSkills.map((skill) => ({
      ...skill,
      label: skill.name,
      value: skill.uuid,
    }));

    return allOptions.map((option) => {
      const skill = skills.find((skillItem) => skillItem.uuid === option.value);
      if (skill) {
        return { ...option, description: skill.description };
      }
      return option;
    });
  }, [allSkills, skills]);

  const handlePostSkills = async (skills: any) => {
    let newlyCreatedSkills = [];

    if (skillsCreated.length > 0) {
      const createSkillPromises = skillsCreated.map((skill) =>
        createSkill({
          name: skill.value,
          certification: skill.certification,
          software: skill.software,
        })
      );

      const createdSkillsResponses = await Promise.all(createSkillPromises);

      newlyCreatedSkills = createdSkillsResponses.map((response: any) => ({
        uuid: response.data.uuid,
        value: response.data.uuid,
        label: response.data.name,
        name: response.data.name,
        isCreated: false,
        approved: false,
      }));
    }

    const finalSkills = skills.map((skill) => {
      if (skill.isCreated) {
        const newlyCreatedSkill = newlyCreatedSkills.find(
          (createdSkill) => createdSkill.name === skill.name || createdSkill.name === skill.label
        );

        if (!newlyCreatedSkill) {
          return skill;
        }

        return {
          ...skill,
          uuid: newlyCreatedSkill.uuid,
          value: newlyCreatedSkill.uuid,
          label: newlyCreatedSkill.name,
          name: newlyCreatedSkill.name,
        };
      }

      return skill;
    });

    const response = await postSkills({
      collection: finalSkills.map((skill) => ({
        uuid: skill.uuid,
        description: skill.description,
      })),
    });

    if (!response.ok) {
      throw new Error(response.originalError?.message);
    }

    const allAvailableSkills = [...allSkills, ...newlyCreatedSkills];
    dispatch(setAllSkills(allAvailableSkills));
    dispatch(setSkills(finalSkills));
    return response.data;
  };

  const { mutate } = useMutation(handlePostSkills, drawerRef.current?.handleMutation);

  const handleSubmit = (values) => {
    mutate(optionsPicked);
  };

  const isSameOption = useCallback((optionA, optionB) => {
    if (!optionA || !optionB) return false;

    if (optionA.uuid && optionB.uuid) {
      return optionA.uuid === optionB.uuid;
    }

    if (optionA.value && optionB.value) {
      return optionA.value === optionB.value;
    }

    return optionA.name === optionB.name;
  }, []);

  const getOptionType = useCallback((option: any): ProfileEditorDrawerSkillsType => {
    if (option?.certification) {
      return PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION;
    }
    if (option?.software) {
      return PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE;
    }
    return PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL;
  }, []);

  const handleOptionsChanged = (options: any[], selectedOption?: any) => {
    let finalOptions = options.map((option) => {
      const existingOption = optionsPicked.find((p) => isSameOption(p, option));
      if (existingOption) {
        return existingOption;
      }
      return { ...option, name: option.label };
    });

    const brandNewOption = finalOptions.filter(
      (option) => !optionsPicked.some((p) => isSameOption(p, option))
    )[0];

    let brandNewType: ProfileEditorDrawerSkillsType | null = null;
    let targetOption: any = selectedOption;
    let pendingTypeSwitch: ProfileEditorDrawerSkillsType | null = null;
    let pendingHighlightIdentifier: string | null = null;
    let limitPrevented = false;

    const selectedOptionExists = selectedOption
      ? optionsPicked.some((p) => isSameOption(p, selectedOption))
      : false;
    const selectedOptionStillSelected = selectedOption
      ? options.some((option) => isSameOption(option, selectedOption))
      : false;
    const isRemoval = selectedOptionExists && !selectedOptionStillSelected;

    if (isRemoval) {
      pendingTypeSwitch = null;
      pendingHighlightIdentifier = null;
      targetOption = null;
    }

    if (brandNewOption) {
      brandNewOption.timestamp = new Date().getTime();
      brandNewType = getOptionType(brandNewOption);
      targetOption = brandNewOption;
      pendingTypeSwitch = brandNewType;
      pendingHighlightIdentifier =
        brandNewOption.uuid || brandNewOption.value || brandNewOption.name;
    }

    finalOptions = finalOptions.map((option) => {
      if (brandNewOption && isSameOption(option, brandNewOption)) {
        return brandNewOption;
      }
      return option;
    });

    // if a skill is in optionsPicked, but not in finalOptions, remove it from optionsPicked (only if it belongs to the current type)
    const shouldFilterByCurrentType =
      !brandNewOption || (brandNewType && brandNewType === currentType) || limitPrevented;

    const tempOptionsPicked = shouldFilterByCurrentType
      ? optionsPicked.filter((option) => {
          if (isRemoval && selectedOption && isSameOption(option, selectedOption)) {
            return false;
          }

          const optionType = getOptionType(option);
          const finalOptionsIncludeOptionType = finalOptions.some(
            (finalOption) => getOptionType(finalOption) === optionType
          );

          if (!finalOptionsIncludeOptionType) {
            return true;
          }

          return finalOptions.some((finalOption) => isSameOption(finalOption, option));
        })
      : optionsPicked;

    let uniqueFinalOptionsPicked = [...finalOptions, ...tempOptionsPicked, ...skillsCreated]
      .filter(
        (option, index, self) =>
          index ===
          self.findIndex((t) => {
            return isSameOption(option, t);
          })
      )
      .sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return b.timestamp - a.timestamp;
        }
        if (a.timestamp) {
          return -1;
        }
        if (b.timestamp) {
          return 1;
        }
        return 0;
      });

    const enforceLimitForType = (type: ProfileEditorDrawerSkillsType, limit: number) => {
      const count = uniqueFinalOptionsPicked.filter(
        (option) => getOptionType(option) === type
      ).length;

      if (count <= limit) {
        return;
      }

      if (targetOption && getOptionType(targetOption) === type) {
        finalOptions = finalOptions.filter((option) => !isSameOption(option, targetOption));
        uniqueFinalOptionsPicked = uniqueFinalOptionsPicked.filter(
          (option) => !isSameOption(option, targetOption)
        );
      } else {
        let currentCount = 0;
        uniqueFinalOptionsPicked = uniqueFinalOptionsPicked.filter((option) => {
          const optionType = getOptionType(option);
          if (optionType === type) {
            if (currentCount >= limit) {
              return false;
            }
            currentCount += 1;
          }
          return true;
        });
        finalOptions = finalOptions.filter((option) =>
          uniqueFinalOptionsPicked.some((opt) => isSameOption(opt, option))
        );
      }

      limitPrevented = true;
      if (brandNewType === type) {
        brandNewType = null;
      }
      pendingTypeSwitch = null;
      pendingHighlightIdentifier = null;
      targetOption = null;
    };

    enforceLimitForType(PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL, MAX_BASIC_SKILLS);
    enforceLimitForType(PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE, MAX_SOFTWARE_SKILLS);
    enforceLimitForType(
      PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION,
      MAX_CERTIFICATIONS_SKILLS
    );

    if (!limitPrevented && brandNewOption && brandNewOption.isCreated) {
      const uniqueSkillsCreated = [...skillsCreated, brandNewOption].filter(
        (option, index, self) =>
          index ===
          self.findIndex((t) =>
            t.uuid && option.uuid ? t.uuid === option.uuid : t.label === option.label
          )
      );
      setSkillsCreated(uniqueSkillsCreated);
    }

    if (isRemoval && selectedOption) {
      setSkillsCreated((prev) => prev.filter((option) => !isSameOption(option, selectedOption)));
      if (highlightedSkillIdentifier) {
        const selectedIdentifier =
          selectedOption.uuid || selectedOption.value || selectedOption.name;
        if (selectedIdentifier === highlightedSkillIdentifier) {
          setHighlightedSkillIdentifier(null);
        }
      }
    }

    setOptionsPicked(uniqueFinalOptionsPicked);

    if (formikRef.current) {
      const skillsCollection = uniqueFinalOptionsPicked
        .filter((option) => !option.certification && !option.software)
        .map((option) => option.uuid);
      const certificationsCollection = uniqueFinalOptionsPicked
        .filter((option) => option.certification)
        .map((option) => option.uuid);
      const softwareCollection = uniqueFinalOptionsPicked
        .filter((option) => option.software)
        .map((option) => option.uuid);

      formikRef.current.setFieldValue('skills_collection', skillsCollection);
      formikRef.current.setFieldValue('certifications_collection', certificationsCollection);
      formikRef.current.setFieldValue('software_collection', softwareCollection);
    }

    if (!brandNewOption && targetOption) {
      const targetExists = uniqueFinalOptionsPicked.some((option) =>
        isSameOption(option, targetOption)
      );

      if (!targetExists) {
        setHighlightedSkillIdentifier(null);
        return;
      }

      if (!limitPrevented) {
        const targetType = getOptionType(targetOption);
        if (targetType !== currentType) {
          setCurrentType(targetType);
        }
        const targetIdentifier = targetOption.uuid || targetOption.value || targetOption.name;
        setHighlightedSkillIdentifier(targetIdentifier);
      }

      return;
    }

    if (!limitPrevented && pendingTypeSwitch && pendingTypeSwitch !== currentType) {
      setCurrentType(pendingTypeSwitch);
    }

    if (!limitPrevented && pendingHighlightIdentifier) {
      setHighlightedSkillIdentifier(pendingHighlightIdentifier);
    }
  };

  useEffect(() => {
    if (!highlightedSkillIdentifier) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHighlightedSkillIdentifier(null);
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [highlightedSkillIdentifier]);

  return (
    <ProfileEditorDrawerSkillsContext.Provider
      value={{
        currentType,
        setCurrentType,
        optionsPicked,
        setOptionsPicked,
        skillsCreated,
        setSkillsCreated,
        options,
        handleOptionsChanged,
        highlightedSkillIdentifier,
        setHighlightedSkillIdentifier,
      }}
    >
      <Formik
        initialValues={{
          collection: skills.map((skill) => skill.uuid),
        }}
        validationSchema={skillsValidationSchema}
        innerRef={formikRef}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form>
            <ProfileEditorDrawer
              header={<ProfileEditorDrawerSkillsHeader />}
              bottom={
                <ProfileEditorDrawerSkillsBottom
                  optionsPicked={optionsPicked}
                  currentType={currentType}
                />
              }
              onClose={onClose}
              needsSaving={props.dirty}
              ref={drawerRef}
            >
              <ProfileEditorDrawerSkillsTypeSwitcher />
              <Suspense fallback={<div />}>
                {currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL && <SkillsBasic />}
                {currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE && <SkillsSoftware />}
                {currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION && (
                  <SkillsCertifications />
                )}
              </Suspense>
            </ProfileEditorDrawer>
          </Form>
        )}
      </Formik>
    </ProfileEditorDrawerSkillsContext.Provider>
  );
}

function ProfileEditorDrawerSkillsHeader() {
  return (
    <ProfileEditorDrawerSkillsHeaderWrapper>
      <ProfileEditorDrawerSkillsHeaderContainer>
        <SkillIcon width={24} height={24} fill={'var(--color-primary)'} />
        <ProfileEditorDrawerTitle>My Expertise</ProfileEditorDrawerTitle>
      </ProfileEditorDrawerSkillsHeaderContainer>
      <ProfileEditorDrawerSkillsHeaderDescription>
        These skills will help us find common ground for you with other people around kyu OS. On
        your public profile, these skills will be grouped into categories that people across the
        companies also share.
      </ProfileEditorDrawerSkillsHeaderDescription>
    </ProfileEditorDrawerSkillsHeaderWrapper>
  );
}

function ProfileEditorDrawerSkillsTypeSwitcher() {
  const { currentType, setCurrentType } = useContext(ProfileEditorDrawerSkillsContext);

  const handleTypeSwitcherClick = (type: ProfileEditorDrawerSkillsType) => {
    setCurrentType(type);
  };

  return (
    <LayoutGroup>
      <ProfileEditorDrawerSkillsTypeSwitcherWrapper>
        <ProfileEditorDrawerSkillsTypeSwitcherContainer
          onClick={() => handleTypeSwitcherClick(PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL)}
        >
          <AsteriskIcon
            width={16}
            height={16}
            fill={
              currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL
                ? 'var(--color-primary)'
                : 'rgba(23, 28, 51, 0.50)'
            }
          />
          <ProfileEditorDrawerSkillsTypeSwitcherLabel
            current={currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL}
          >
            Skills
          </ProfileEditorDrawerSkillsTypeSwitcherLabel>
          {currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL && (
            <ProfileEditorDrawerSkillsTypeSwitcherUnderline layoutId="underline" />
          )}
        </ProfileEditorDrawerSkillsTypeSwitcherContainer>
        <ProfileEditorDrawerSkillsTypeSwitcherContainer
          onClick={() => handleTypeSwitcherClick(PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE)}
        >
          <CodeBrowserIcon
            width={16}
            height={16}
            fill={
              currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE
                ? 'var(--color-primary)'
                : 'rgba(23, 28, 51, 0.50)'
            }
          />
          <ProfileEditorDrawerSkillsTypeSwitcherLabel
            current={currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE}
          >
            Software
          </ProfileEditorDrawerSkillsTypeSwitcherLabel>
          {currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE && (
            <ProfileEditorDrawerSkillsTypeSwitcherUnderline layoutId="underline" />
          )}
        </ProfileEditorDrawerSkillsTypeSwitcherContainer>
        <ProfileEditorDrawerSkillsTypeSwitcherContainer
          onClick={() => handleTypeSwitcherClick(PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION)}
        >
          <CheckVerifiedIcon
            width={16}
            height={16}
            fill={
              currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION
                ? 'var(--color-primary)'
                : 'rgba(23, 28, 51, 0.50)'
            }
          />
          <ProfileEditorDrawerSkillsTypeSwitcherLabel
            current={currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION}
          >
            Certifications
          </ProfileEditorDrawerSkillsTypeSwitcherLabel>
          {currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION && (
            <ProfileEditorDrawerSkillsTypeSwitcherUnderline layoutId="underline" />
          )}
        </ProfileEditorDrawerSkillsTypeSwitcherContainer>
      </ProfileEditorDrawerSkillsTypeSwitcherWrapper>
    </LayoutGroup>
  );
}
interface ProfileEditorDrawerSkillsBottomProps {
  optionsPicked: any[];
  currentType: ProfileEditorDrawerSkillsType;
}

function ProfileEditorDrawerSkillsBottom({
  optionsPicked,
  currentType,
}: ProfileEditorDrawerSkillsBottomProps) {
  const { isValid, submitForm, isSubmitting } = useFormikContext();

  const maxSkillsByType = useMemo(() => {
    switch (currentType) {
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL:
        return MAX_BASIC_SKILLS;
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE:
        return MAX_SOFTWARE_SKILLS;
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION:
        return MAX_CERTIFICATIONS_SKILLS;
      default:
        return MAX_SKILLS;
    }
  }, [currentType]);

  const optionsByType = useMemo(() => {
    switch (currentType) {
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL:
        return optionsPicked.filter((option) => !option.certification && !option.software);
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE:
        return optionsPicked.filter((option) => option.software);
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION:
        return optionsPicked.filter((option) => option.certification);
      default:
        return optionsPicked;
    }
  }, [optionsPicked, currentType]);

  return (
    <ProfileEditorDrawerSkillsBottomWrapper>
      <ProfileEditorDrawerSkillsBottomText reachedMax={optionsByType?.length > maxSkillsByType}>
        {optionsByType?.length} of {maxSkillsByType} selected
      </ProfileEditorDrawerSkillsBottomText>
      <ProfileEditorDrawerPublishButton
        disabled={
          !isValid ||
          isSubmitting ||
          optionsByType?.length > maxSkillsByType ||
          optionsPicked.length > MAX_SKILLS
        }
        type="submit"
        onClick={submitForm}
      >
        Publish
      </ProfileEditorDrawerPublishButton>
    </ProfileEditorDrawerSkillsBottomWrapper>
  );
}

interface ProfileEditorDrawerSkillsFeelingStuckProps {
  handlePickedSkill: (skill: any) => void;
  optionsPicked: any[];
  currentType: ProfileEditorDrawerSkillsType;
}

export function ProfileEditorDrawerSkillsFeelingStuck({
  handlePickedSkill,
  optionsPicked,
  currentType,
}: ProfileEditorDrawerSkillsFeelingStuckProps) {
  const [currentlyShowing, setCurrentlyShowing] = useState<'random' | 'all'>('random');

  if (currentlyShowing === 'random') {
    return (
      <ProfileEditorDrawerSkillsFeelingStuckRandom
        setCurrentlyShowing={setCurrentlyShowing}
        handlePickedSkill={handlePickedSkill}
        optionsPicked={optionsPicked}
        currentType={currentType}
      />
    );
  }

  if (currentlyShowing === 'all') {
    return (
      <ProfileEditorDrawerSkillsFeelingStuckAll
        setCurrentlyShowing={setCurrentlyShowing}
        handlePickedSkill={handlePickedSkill}
        optionsPicked={optionsPicked}
        currentType={currentType}
      />
    );
  }

  return (
    <ProfileEditorDrawerSkillsFeelingStuckWrapper>
      <ProfileEditorDrawerSkillsFeelingStuckTitle>
        Feeling Stuck?
      </ProfileEditorDrawerSkillsFeelingStuckTitle>
      <ProfileEditorDrawerSkillsFeelingStuckContainer>
        <ProfileEditorDrawerSkillsFeelingStuckButton onClick={() => setCurrentlyShowing('random')}>
          <Sparkle width={24} height={24} fill={'var(--color-primary)'} />
          <ProfileEditorDrawerSkillsFeelingStuckButtonText>
            Randomize
          </ProfileEditorDrawerSkillsFeelingStuckButtonText>
        </ProfileEditorDrawerSkillsFeelingStuckButton>
        <ProfileEditorDrawerSkillsFeelingStuckButton onClick={() => setCurrentlyShowing('all')}>
          <RightArrow width={12} height={12} fill={'var(--color-primary)'} />
          <ProfileEditorDrawerSkillsFeelingStuckButtonText>
            See All
          </ProfileEditorDrawerSkillsFeelingStuckButtonText>
        </ProfileEditorDrawerSkillsFeelingStuckButton>
      </ProfileEditorDrawerSkillsFeelingStuckContainer>
    </ProfileEditorDrawerSkillsFeelingStuckWrapper>
  );
}

interface ProfileEditorDrawerSkillsFeelingStuckOptionProps {
  setCurrentlyShowing: (value: 'random' | 'all') => void;
  handlePickedSkill: (skill: any) => void;
  optionsPicked: any[];
  currentType: ProfileEditorDrawerSkillsType;
}

function ProfileEditorDrawerSkillsFeelingStuckRandom({
  handlePickedSkill,
  setCurrentlyShowing,
  optionsPicked,
  currentType,
}: ProfileEditorDrawerSkillsFeelingStuckOptionProps) {
  const { allSkills } = useSelector(profileDataSelector);

  const reachedMaxSkills = useMemo(() => {
    if (currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL) {
      return optionsPicked.length === MAX_BASIC_SKILLS;
    } else if (currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE) {
      return optionsPicked.length === MAX_SOFTWARE_SKILLS;
    } else if (currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION) {
      return optionsPicked.length === MAX_CERTIFICATIONS_SKILLS;
    }
    return false;
  }, [optionsPicked, currentType]);

  const filteredSkills = useMemo(() => {
    switch (currentType) {
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL:
        return allSkills.filter((skill) => !skill.certification && !skill.software);
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE:
        return allSkills.filter((skill) => skill.software);
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION:
        return allSkills.filter((skill) => skill.certification);
      default:
        return allSkills;
    }
  }, [allSkills, currentType]);

  const randomSkills = useMemo(() => {
    const randomSkills = [];
    const randomSkillsIndexes = [];
    const maxSkills = Math.min(7, filteredSkills.length);

    while (randomSkills.length < maxSkills) {
      const randomIndex = Math.floor(Math.random() * filteredSkills.length);
      if (!randomSkillsIndexes.includes(randomIndex)) {
        randomSkillsIndexes.push(randomIndex);
        randomSkills.push(filteredSkills[randomIndex]);
      }
    }
    return randomSkills;
  }, [filteredSkills]);

  return (
    <ProfileEditorDrawerSkillsFeelingStuckRandomContainer>
      <ProfileEditorDrawerSkillsFeelingStuckRandomPillsContainer>
        {randomSkills.map((skill, i) => {
          const index = optionsPicked.findIndex((option) => option.uuid === skill.uuid);

          return (
            <ProfileEditDataTypePill
              key={`${skill.name}-${i}`}
              type={index !== -1 ? 'selected' : reachedMaxSkills ? 'disabled' : 'unselected'}
              label={skill.name}
              approved={skill.approved}
              peopleCount={skill.people_count}
              onClick={() => handlePickedSkill({ ...skill, label: skill.name })}
            />
          );
        })}
      </ProfileEditorDrawerSkillsFeelingStuckRandomPillsContainer>
      <ProfileEditorDrawerSkillsFeelingStuckRandomShowMoreContainer>
        <Sparkle width={24} height={24} fill={'var(--color-primary)'} />
        <ProfileEditorDrawerSkillsFeelingStuckRandomShowMoreText
          onClick={() => setCurrentlyShowing('all')}
        >
          SHOW ME MORE
        </ProfileEditorDrawerSkillsFeelingStuckRandomShowMoreText>
      </ProfileEditorDrawerSkillsFeelingStuckRandomShowMoreContainer>
    </ProfileEditorDrawerSkillsFeelingStuckRandomContainer>
  );
}

function ProfileEditorDrawerSkillsFeelingStuckAll({
  handlePickedSkill,
  setCurrentlyShowing,
  optionsPicked,
  currentType,
}: ProfileEditorDrawerSkillsFeelingStuckOptionProps) {
  const { allSkills } = useSelector(profileDataSelector);
  const [currentCategory, setCurrentCategory] = useState('A-C');

  const reachedMaxSkills = useMemo(() => {
    if (currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL) {
      return optionsPicked.length === MAX_BASIC_SKILLS;
    } else if (currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE) {
      return optionsPicked.length === MAX_SOFTWARE_SKILLS;
    } else if (currentType === PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION) {
      return optionsPicked.length === MAX_CERTIFICATIONS_SKILLS;
    }
    return false;
  }, [optionsPicked, currentType]);

  const filteredSkills = useMemo(() => {
    switch (currentType) {
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SKILL:
        return allSkills.filter((skill) => !skill.certification && !skill.software);
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.SOFTWARE:
        return allSkills.filter((skill) => skill.software);
      case PROFILE_EDITOR_DRAWER_SKILLS_TYPES.CERTIFICATION:
        return allSkills.filter((skill) => skill.certification);
      default:
        return [];
    }
  }, [allSkills, currentType]);

  const sortedSkills = useMemo(() => {
    const categorizedObjects = {
      'A-C': [],
      'D-F': [],
      'G-I': [],
      'J-L': [],
      'M-P': [],
      'Q-S': [],
      'T-V': [],
      'W-Z': [],
    };

    for (const object of filteredSkills) {
      const firstLetter = object.name[0].toUpperCase();
      if (firstLetter >= 'A' && firstLetter <= 'C') {
        categorizedObjects['A-C'].push(object);
      } else if (firstLetter >= 'D' && firstLetter <= 'F') {
        categorizedObjects['D-F'].push(object);
      } else if (firstLetter >= 'G' && firstLetter <= 'I') {
        categorizedObjects['G-I'].push(object);
      } else if (firstLetter >= 'J' && firstLetter <= 'L') {
        categorizedObjects['J-L'].push(object);
      } else if (firstLetter >= 'M' && firstLetter <= 'P') {
        categorizedObjects['M-P'].push(object);
      } else if (firstLetter >= 'Q' && firstLetter <= 'S') {
        categorizedObjects['Q-S'].push(object);
      } else if (firstLetter >= 'T' && firstLetter <= 'V') {
        categorizedObjects['T-V'].push(object);
      } else if (firstLetter >= 'W' && firstLetter <= 'Z') {
        categorizedObjects['W-Z'].push(object);
      }
    }

    return categorizedObjects;
  }, [filteredSkills]);

  useEffect(() => {
    if (sortedSkills['A-C'].length > 0) {
      setCurrentCategory('A-C');
    } else if (sortedSkills['D-F'].length > 0) {
      setCurrentCategory('D-F');
    } else if (sortedSkills['G-I'].length > 0) {
      setCurrentCategory('G-I');
    } else if (sortedSkills['J-L'].length > 0) {
      setCurrentCategory('J-L');
    } else if (sortedSkills['M-P'].length > 0) {
      setCurrentCategory('M-P');
    } else if (sortedSkills['Q-S'].length > 0) {
      setCurrentCategory('Q-S');
    } else if (sortedSkills['T-V'].length > 0) {
      setCurrentCategory('T-V');
    } else if (sortedSkills['W-Z'].length > 0) {
      setCurrentCategory('W-Z');
    }
  }, [sortedSkills]);

  return (
    <ProfileEditorDrawerSkillsFeelingStuckAllContainer>
      <ProfileEditorDrawerSkillsFeelingStuckAllCategoriesContainer>
        {Object.keys(sortedSkills).map((key, index) => {
          if (sortedSkills[key].length === 0) return <></>;

          return (
            <ProfileEditorDrawerSkillsFeelingStuckAllCategory
              key={`${key}-${index}`}
              onClick={() => setCurrentCategory(key)}
              current={currentCategory === key}
            >
              {key}
            </ProfileEditorDrawerSkillsFeelingStuckAllCategory>
          );
        })}
      </ProfileEditorDrawerSkillsFeelingStuckAllCategoriesContainer>
      <ProfileEditorDrawerSkillsFeelingStuckAllPillsContainer>
        {sortedSkills[currentCategory].map((skill, i) => {
          const index = optionsPicked.findIndex((option) => option.uuid === skill.uuid);

          return (
            <ProfileEditDataTypePill
              key={`${skill.name}-${i}`}
              type={index !== -1 ? 'selected' : reachedMaxSkills ? 'disabled' : 'unselected'}
              label={skill.name}
              approved={skill.approved}
              peopleCount={skill.people_count}
              onClick={() => handlePickedSkill({ ...skill, label: skill.name })}
            />
          );
        })}
      </ProfileEditorDrawerSkillsFeelingStuckAllPillsContainer>
    </ProfileEditorDrawerSkillsFeelingStuckAllContainer>
  );
}
